import React, { useState } from "react";
import { Heart, CreditCard, Building2, BookOpen, Radio, Globe, ShieldCheck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import GoldCross from "@/components/GoldCross";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const QUICK = [10, 25, 50, 100];

const reasons = [
  { icon: Radio, title: "Annoncer l'Évangile", text: "Diffuser la Parole de Dieu à travers les médias et les nations." },
  { icon: BookOpen, title: "Produire des enseignements", text: "Créer des contenus audio, vidéo et littéraires édifiants." },
  { icon: Globe, title: "Impacter les nations", text: "Soutenir des actions qui transforment des vies dans le monde entier." },
];

const PAYPAL_ME = "https://paypal.me/ESKALATORBYFAITH";

const Donate = () => {
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");

  const selected = custom ? custom : amount;

  const handleDonate = (method) => {
    const value = parseFloat(selected);
    if (!value || value <= 0) {
      toast.error("Veuillez choisir un montant valide.");
      return;
    }
    const url = `${PAYPAL_ME}/${value.toFixed(2)}EUR`;
    toast.success(`Redirection vers PayPal pour un don de ${value.toFixed(2)} €`, {
      description: "Merci pour votre générosité ! 🙏",
    });
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      try {
        window.top.location.href = url;
      } catch {
        window.location.href = url;
      }
    }
  };

  return (
    <div>
      <Seo
        title="Faire un don — Soutenir l'œuvre"
        description="Soutenez le Ministère international Prophète Steeve Orphôss. Votre don contribue à la diffusion de l'Évangile, à la production d'enseignements bibliques et à l'impact du ministère dans les nations."
        path="/faire-un-don"
      />
      <PageHeader
        overline="Partenariat & Générosité"
        title="Soutenir l'œuvre"
        subtitle="Votre don contribue à la diffusion de l'Évangile, à la production de contenus spirituels, aux enseignements et à l'impact du ministère dans les nations."
      />

      {/* Donation card */}
      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brown-dark p-8 md:p-12" data-testid="donation-card">
              <div className="god-rays absolute inset-0 opacity-50" />
              <GoldCross className="absolute -right-8 top-1/2 -translate-y-1/2 h-[150%] w-72 animate-glow-pulse" opacity={0.1} />
              <div className="relative">
                <h2 className="font-heading text-3xl md:text-4xl font-light text-ivory mb-2">Choisissez votre montant</h2>
                <p className="text-ivory/60 mb-8">Chaque don, petit ou grand, fait une différence éternelle.</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  {QUICK.map((a) => (
                    <button
                      key={a}
                      onClick={() => { setAmount(a); setCustom(""); }}
                      data-testid={`donate-amount-${a}`}
                      className={`py-4 rounded-xl font-heading text-2xl font-semibold transition-all duration-300 ${
                        !custom && amount === a
                          ? "bg-gold text-white shadow-lg shadow-gold/30"
                          : "bg-white/5 text-ivory border border-gold/30 hover:border-gold hover:bg-white/10"
                      }`}
                    >
                      {a} €
                    </button>
                  ))}
                </div>

                <div className="mb-8">
                  <label className="block text-xs uppercase tracking-[0.2em] text-gold-light/80 font-semibold mb-2">Montant libre</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      placeholder="Entrez un montant"
                      data-testid="donate-custom-amount"
                      className="w-full bg-white/5 border border-gold/30 focus:border-gold rounded-xl py-4 px-5 pr-12 text-ivory text-lg placeholder:text-ivory/30 focus:outline-none transition-colors"
                    />
                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-gold-light text-lg font-semibold">€</span>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  <button onClick={() => handleDonate("carte bancaire")} data-testid="donate-card-button" className="inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-all duration-300">
                    <CreditCard size={17} /> Carte bancaire
                  </button>
                  <button onClick={() => handleDonate("PayPal")} data-testid="donate-paypal-button" className="inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-white text-brown-dark font-semibold hover:bg-gold-light transition-all duration-300">
                    <span className="font-bold"><span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span></span>
                  </button>
                  <button onClick={() => handleDonate("virement")} data-testid="donate-transfer-button" className="inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full border border-gold text-gold-light font-semibold hover:bg-gold/10 transition-all duration-300">
                    <Building2 size={17} /> Virement
                  </button>
                </div>
                <p className="flex items-center gap-2 text-xs text-ivory/50 mt-6">
                  <ShieldCheck size={14} className="text-gold" /> Votre paiement est sécurisé et encrypted.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why donate */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#FBF6E9] to-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4">Votre impact</p>
              <h2 className="font-heading text-4xl md:text-5xl font-light text-brown-black tracking-tight">Pourquoi donner ?</h2>
              <div className="gold-line w-24 mx-auto mt-8" />
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {reasons.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.1}>
                <div className="h-full bg-white rounded-2xl p-9 border border-transparent hover:border-gold transition-all duration-300 hover:-translate-y-1 text-center" style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.05)" }} data-testid={`donate-reason-${i}`}>
                  <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-gold/10 text-gold mb-6">
                    <r.icon size={28} />
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-brown-black mb-3">{r.title}</h3>
                  <p className="text-brown-black/65 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-14">
            <p className="font-heading text-2xl md:text-3xl italic text-brown-dark">
              « Il y a plus de bonheur à donner qu'à recevoir » <span className="text-gold not-italic text-lg">— Actes 20:35</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
